import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ShieldCheck, AlertTriangle, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const errText = (d) => (typeof d === "string" ? d : "Si è verificato un errore.");
const fmtY = (iso) => (iso ? new Date(iso).getFullYear() : null);

export default function TesterOnboard() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "", confirm: "", stable_name: "" });
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState("form"); // form | legacy | done
  const [stableId, setStableId] = useState(null);
  const [cands, setCands] = useState([]);
  const [sel, setSel] = useState({});
  const [corr, setCorr] = useState({});
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get(`/public/tester-invite/${token}`)
      .then(({ data }) => { if (data.status !== "available") setError("expired"); setInvite(data); })
      .catch(() => setError("invalid")).finally(() => setLoading(false));
  }, [token]);

  const goDashboard = () => { window.location.href = "/scuderie"; };

  const mismatch = form.confirm.length > 0 && form.password !== form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (form.password !== form.confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setSaving(true);
    try {
      const { confirm, ...payload } = form;
      const { data } = await api.post(`/public/tester-invite/${token}/accept`, payload);
      if (data.token) localStorage.setItem("equiro_token", data.token);
      setStableId(data.stable_id);
      const pkg = await api.get(`/stables/${data.stable_id}/legacy-import`).then((r) => r.data.package).catch(() => null);
      if (pkg && pkg.candidates?.length) {
        setCands(pkg.candidates);
        const init = {}; pkg.candidates.forEach((c) => { init[c.candidate_id] = !!c.importable; });
        setSel(init);
        setStep("legacy");
        toast.success("Benvenuto! Abbiamo recuperato i cavalli del tuo precedente Equiro.");
      } else {
        toast.success("Benvenuto in Equiro!");
        goDashboard();
      }
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const effImportable = (c) => c.importable || !!(corr[c.candidate_id] || "").trim();
  const setAll = (v) => { const n = {}; cands.forEach((c) => { n[c.candidate_id] = v && effImportable(c); }); setSel(n); };
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const selectedCount = cands.filter((c) => sel[c.candidate_id]).length;

  const doImport = async () => {
    setImporting(true);
    try {
      const selected_ids = cands.filter((c) => sel[c.candidate_id]).map((c) => c.candidate_id);
      const corrections = {}; Object.entries(corr).forEach(([k, v]) => { if ((v || "").trim()) corrections[k] = v.trim(); });
      const { data } = await api.post(`/stables/${stableId}/legacy-import/import`, { selected_ids, corrections });
      setSummary(data.summary); setStep("done");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };
  const doSkip = async () => {
    try { await api.post(`/stables/${stableId}/legacy-import/skip`); } catch { /* noop */ }
    toast.success("Potrai importare i cavalli in seguito dalle Impostazioni.");
    goDashboard();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0F241A]"><Loader2 className="h-6 w-6 animate-spin text-[#C9A227]" /></div>;

  const invalid = error || (invite && invite.status !== "available");
  if (invalid && step === "form") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F241A] px-4">
        <div className="max-w-md w-full rounded-2xl bg-[#152E22] border border-[#25402F] p-8 text-center" data-testid="tester-invite-invalid">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
          <h1 className="font-display text-xl font-semibold text-[#EAE4D3]">Invito non disponibile</h1>
          <p className="text-sm text-[#8FA396] mt-2">Questo link non è più valido. Chiedi al tuo referente Equiro un nuovo invito.</p>
        </div>
      </div>
    );
  }

  if (step === "legacy") {
    return (
      <div className="min-h-screen bg-[#0F241A] px-4 py-8">
        <div className="max-w-2xl mx-auto" data-testid="legacy-import-step">
          <div className="flex items-center gap-2 mb-1"><Sparkles className="h-5 w-5 text-[#C9A227]" /><span className="font-mono-label text-[10px] uppercase text-[#C9A227]">Importa dal tuo precedente Equiro</span></div>
          <h1 className="font-display text-2xl font-bold text-[#EAE4D3]">Abbiamo recuperato i cavalli del tuo precedente Equiro</h1>
          <p className="text-sm text-[#8FA396] mt-1 mb-4">Seleziona quelli che fanno ancora parte della tua scuderia. Potrai aggiungerne altri successivamente.</p>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-[#EAE4D3]" data-testid="legacy-selected-count">{selectedCount} selezionati · {cands.length} totali</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="legacy-select-all" onClick={() => setAll(true)} className="border-[#25402F] text-[#EAE4D3] hover:bg-[#1B3826]">Seleziona tutti</Button>
              <Button size="sm" variant="outline" data-testid="legacy-deselect-all" onClick={() => setAll(false)} className="border-[#25402F] text-[#EAE4D3] hover:bg-[#1B3826]">Deseleziona tutti</Button>
            </div>
          </div>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {cands.map((c) => {
              const badName = c.issues?.includes("nome_mancante") && !(corr[c.candidate_id] || "").trim();
              const canSel = effImportable(c);
              return (
                <div key={c.candidate_id} data-testid={`legacy-cand-${c.candidate_id}`}
                  className={`rounded-xl border px-3 py-2.5 flex items-start gap-3 ${sel[c.candidate_id] ? "border-[#C9A227]/60 bg-[#17301F]" : "border-[#25402F] bg-[#152E22]"}`}>
                  <input type="checkbox" data-testid={`legacy-sel-${c.candidate_id}`} disabled={!canSel} checked={!!sel[c.candidate_id]}
                    onChange={() => toggle(c.candidate_id)} className="mt-1 h-4 w-4 rounded accent-[#C9A227] shrink-0 disabled:opacity-40" />
                  <div className="min-w-0 flex-1">
                    {badName ? (
                      <div className="flex items-center gap-2">
                        <Input data-testid={`legacy-fix-name-${c.candidate_id}`} placeholder="Nome mancante — correggi per importare"
                          value={corr[c.candidate_id] || ""} onChange={(e) => setCorr((s) => ({ ...s, [c.candidate_id]: e.target.value }))}
                          className="h-8 bg-[#0F241A] border-[#25402F] text-[#EAE4D3] text-sm" />
                      </div>
                    ) : (
                      <p className="font-semibold text-[#EAE4D3] truncate">{corr[c.candidate_id]?.trim() || c.name}</p>
                    )}
                    <p className="text-[11px] text-[#8FA396] mt-0.5 flex flex-wrap gap-x-2">
                      {c.sex && <span>{c.sex}</span>}
                      {fmtY(c.dob) && <span>{fmtY(c.dob)}</span>}
                      {c.breed && <span>{c.breed}</span>}
                      {c.microchip && <span className="font-mono-label">chip {c.microchip}</span>}
                    </p>
                    {c.legacy_status && <p className="text-[10px] text-[#6F8377] mt-0.5" data-testid={`legacy-status-${c.candidate_id}`}>Stato nel precedente Equiro: {c.legacy_status}</p>}
                    {badName && <p className="text-[10px] text-amber-400 mt-0.5">Non importabile finché il nome non è corretto.</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-5">
            <Button onClick={doImport} disabled={importing || selectedCount === 0} data-testid="legacy-import-btn" className="flex-1 bg-[#C9A227] text-[#0F241A] hover:bg-[#b8931f] font-semibold">
              {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Importa cavalli selezionati ({selectedCount})
            </Button>
            <Button onClick={doSkip} variant="outline" data-testid="legacy-skip-btn" className="border-[#25402F] text-[#EAE4D3] hover:bg-[#1B3826]">Salta per ora</Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "done") {
    const imp = (summary?.imported_new_identity || 0) + (summary?.associated_existing_identity || 0);
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F241A] px-4">
        <div className="max-w-md w-full rounded-2xl bg-[#152E22] border border-[#25402F] p-8 text-center" data-testid="legacy-done">
          <div className="h-12 w-12 rounded-full bg-[#C9A227]/20 flex items-center justify-center mx-auto mb-3"><Check className="h-6 w-6 text-[#C9A227]" /></div>
          <h1 className="font-display text-xl font-semibold text-[#EAE4D3]">Import completato</h1>
          <div className="text-sm text-[#8FA396] mt-3 space-y-1">
            <p><span className="text-[#EAE4D3] font-semibold" data-testid="done-imported">{imp}</span> cavalli importati</p>
            <p><span className="text-[#EAE4D3] font-semibold" data-testid="done-skipped">{summary?.skipped_by_user || 0}</span> non selezionati</p>
            {(summary?.needs_identity_review || 0) > 0 && <p><span className="text-amber-400 font-semibold">{summary.needs_identity_review}</span> identità da verificare</p>}
            {(summary?.invalid_candidate || 0) > 0 && <p><span className="text-amber-400 font-semibold">{summary.invalid_candidate}</span> non importabili (nome mancante)</p>}
          </div>
          <Button onClick={goDashboard} data-testid="done-go-dashboard" className="w-full mt-5 bg-[#C9A227] text-[#0F241A] hover:bg-[#b8931f] font-semibold">Vai alla Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F241A] px-4 py-10">
      <div className="max-w-md w-full rounded-2xl bg-[#152E22] border border-[#25402F] p-8" data-testid="tester-onboard-form">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck className="h-5 w-5 text-[#C9A227]" /><span className="font-mono-label text-[10px] uppercase text-[#C9A227]">Invito Tester Equiro</span></div>
        <h1 className="font-display text-2xl font-bold text-[#EAE4D3]">Crea la tua scuderia</h1>
        <p className="text-sm text-[#8FA396] mt-1 mb-5">Bastano pochi dati per iniziare. Potrai completare tutto in seguito.</p>
        <form onSubmit={submit} className="space-y-3" autoComplete="off">
          <div>
            <Label className="text-xs text-[#8FA396]">Email invitata</Label>
            <Input data-testid="onboard-email" name="tester_invite_email" value={invite?.recipient_email || ""} readOnly disabled autoComplete="off" className="mt-1 bg-[#0F241A] border-[#25402F] text-[#8FA396]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#8FA396]">Nome</Label><Input data-testid="onboard-first" name="given-name" autoComplete="given-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required className="mt-1 bg-[#0F241A] border-[#25402F] text-[#EAE4D3]" /></div>
            <div><Label className="text-xs text-[#8FA396]">Cognome</Label><Input data-testid="onboard-last" name="family-name" autoComplete="family-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required className="mt-1 bg-[#0F241A] border-[#25402F] text-[#EAE4D3]" /></div>
          </div>
          <div><Label className="text-xs text-[#8FA396]">Password (min 8 caratteri)</Label><Input data-testid="onboard-password" name="new-password" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="mt-1 bg-[#0F241A] border-[#25402F] text-[#EAE4D3]" /></div>
          <div>
            <Label className="text-xs text-[#8FA396]">Conferma password</Label>
            <Input data-testid="onboard-confirm" name="confirm-password" type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required minLength={8} className={`mt-1 bg-[#0F241A] border-[#25402F] text-[#EAE4D3] ${mismatch ? "border-red-500" : ""}`} />
            {mismatch && <p className="text-[11px] text-red-400 mt-1" data-testid="onboard-password-mismatch">Le password non coincidono</p>}
          </div>
          <div><Label className="text-xs text-[#8FA396]">Nome della scuderia</Label><Input data-testid="onboard-stable" name="stable-name" autoComplete="off" value={form.stable_name} onChange={(e) => setForm({ ...form, stable_name: e.target.value })} required className="mt-1 bg-[#0F241A] border-[#25402F] text-[#EAE4D3]" /></div>
          <Button type="submit" disabled={saving || mismatch} data-testid="onboard-submit" className="w-full bg-[#C9A227] text-[#0F241A] hover:bg-[#b8931f] font-semibold mt-2">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Crea scuderia e inizia
          </Button>
        </form>
      </div>
    </div>
  );
}
